import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Alert,
  IconButton,
  Tooltip,
  Grid,
  Paper,
} from '@mui/material';
import {
  ExpandMore,
  Info,
  Login,
  Dashboard,
  Assignment,
  Timeline,
  People,
  BarChart,
  Settings,
  Help,
  Close,
  Security,
  Speed,
  Search,
  Notifications,
  Smartphone,
  CheckCircle,
} from '@mui/icons-material';

interface ApplicationUserGuideProps {
  open: boolean;
  onClose: () => void;
}

const ApplicationUserGuide: React.FC<ApplicationUserGuideProps> = ({ open, onClose }) => {
  const [expandedSection, setExpandedSection] = useState<string | false>('panel1');

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Help color="primary" />
            <Typography variant="h6">e-BIRA Application User Guide</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" paragraph>
          Complete guide to using the e-BIRA project and issue management system effectively.
        </Typography>

        {/* Authentication & Access */}
        <Accordion expanded={expandedSection === 'panel1'} onChange={handleAccordionChange('panel1')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Security color="primary" />
              <Typography variant="subtitle1">🔐 Authentication & Access</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              User roles and login process for the e-BIRA system.
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight="bold" color="primary.main">
                  User Roles:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="Admin" 
                      secondary="Full system access, user management" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Approver" 
                      secondary="Review and approve issues" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="CM_PIC" 
                      secondary="Implement counter-measures" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Issuer" 
                      secondary="Create and manage issues" 
                    />
                  </ListItem>
                </List>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight="bold" color="primary.main">
                  Login Process:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="1. Enter email and password" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="2. Click Login to access dashboard" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="3. Use 'Forgot Password' if needed" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="4. Change password via user menu" />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Dashboard & Navigation */}
        <Accordion expanded={expandedSection === 'panel2'} onChange={handleAccordionChange('panel2')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Dashboard color="info" />
              <Typography variant="subtitle1">🏠 Dashboard & Navigation</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              Main interface and navigation structure.
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                Key Navigation Areas:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="/projects" 
                    secondary="Main events dashboard with all accessible events" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="/projects/:id" 
                    secondary="Event detail view and quick actions" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="/projects/:id/lines" 
                    secondary="Production lines management" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="/projects/:id/data-analysis" 
                    secondary="Event analytics and reporting" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="/users" 
                    secondary="User management (Admin only)" 
                  />
                </ListItem>
              </List>
            </Box>

            <Alert severity="info">
              <Typography variant="body2">
                <strong>Tip:</strong> Use the user menu (top-right) to access tickets, change password, and logout.
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* Projects Management */}
        <Accordion expanded={expandedSection === 'panel3'} onChange={handleAccordionChange('panel3')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Assignment color="success" />
              <Typography variant="subtitle1">📊 Events Management</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              Create and manage events with comprehensive tracking.
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight="bold" color="success.main">
                  Event Features:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="• Unique event codes" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Detailed descriptions" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Status tracking (Active/Inactive)" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Issue and line counts" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Creation date tracking" />
                  </ListItem>
                </List>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight="bold" color="success.main">
                  Event Actions:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="• Create new events" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Edit event details" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Manage production lines" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• View analytics" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Delete events (with confirmation)" />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Issue Management */}
        <Accordion expanded={expandedSection === 'panel4'} onChange={handleAccordionChange('panel4')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Timeline color="warning" />
              <Typography variant="subtitle1">🐛 Issue Management System</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              Complete issue tracking workflow from creation to resolution.
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold" color="warning.main">
                Issue Lifecycle:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1, flexWrap: 'wrap' }}>
                <Chip label="Draft" size="small" color="default" />
                <span>→</span>
                <Chip label="Waiting Response" size="small" color="info" />
                <span>→</span>
                <Chip label="In Progress" size="small" color="warning" />
                <span>→</span>
                <Chip label="Waiting Approval" size="small" color="secondary" />
                <span>→</span>
                <Chip label="Completed" size="small" color="success" />
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight="bold">
                  Issue Properties:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="• Type: Product or Process" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Rank: Priority (A, B, C)" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Part information" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Problem description" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Counter-measures" />
                  </ListItem>
                </List>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight="bold">
                  Role Responsibilities:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="• Issuer: Create and document issues" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• CM_PIC: Implement solutions" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Approver: Review and approve" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Admin: System oversight" />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Data Analysis */}
        <Accordion expanded={expandedSection === 'panel5'} onChange={handleAccordionChange('panel5')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <BarChart color="secondary" />
              <Typography variant="subtitle1">📈 Data Analysis & Reporting</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              Comprehensive analytics and reporting capabilities.
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold" color="secondary.main">
                Available Analytics:
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 1, textAlign: 'center' }}>
                    <Typography variant="caption">Total Issues</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 1, textAlign: 'center' }}>
                    <Typography variant="caption">Status Distribution</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 1, textAlign: 'center' }}>
                    <Typography variant="caption">Priority Breakdown</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 1, textAlign: 'center' }}>
                    <Typography variant="caption">Type Analysis</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            <List dense>
              <ListItem>
                <ListItemText primary="• Visual charts (pie, bar, trend)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• PDF report generation" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Data export capabilities" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Performance metrics" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* User Management */}
        <Accordion expanded={expandedSection === 'panel6'} onChange={handleAccordionChange('panel6')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <People color="error" />
              <Typography variant="subtitle1">👥 User Management (Admin Only)</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              Comprehensive user administration and role management.
            </Typography>
            
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Admin Access Required:</strong> Only users with Admin role can access user management features.
              </Typography>
            </Alert>

            <List dense>
              <ListItem>
                <ListItemText primary="• Create new user accounts" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Assign project-specific roles" />
              </ListItem>
                <ListItem>
                <ListItemText primary="• Manage user status (Active/Inactive)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Role-based permissions" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Project access control" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Best Practices */}
        <Accordion expanded={expandedSection === 'panel7'} onChange={handleAccordionChange('panel7')}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" gap={1}>
              <Speed color="info" />
              <Typography variant="subtitle1">💡 Best Practices</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph>
              Tips and recommendations for effective system usage.
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight="bold" color="info.main">
                  Issue Creation:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="• Be specific in descriptions" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Add supporting photos" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Set appropriate priority" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Propose initial solutions" />
                  </ListItem>
                </List>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight="bold" color="info.main">
                  Project Management:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="• Organize lines logically" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Regular issue reviews" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Team training" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="• Data quality maintenance" />
                  </ListItem>
                </List>
              </Grid>
            </Grid>

            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Pro Tip:</strong> Regular reviews and data quality checks ensure the system remains effective and valuable.
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Got it, thanks!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApplicationUserGuide;
